/**
 * Find Cafe Section - Google Maps Integration
 * Vanilla JavaScript port of the original maps.js
 */

/* Map styling - muted colors matching brand */
const findCafeMapStyle = [
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#444444' }]
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [{ color: '#e7e7e5' }]
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [{ saturation: -100 }, { lightness: 45 }]
  },
  {
    featureType: 'road.highway',
    elementType: 'all',
    stylers: [{ visibility: 'simplified' }]
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'off' }]
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [{ color: '#abbfd0' }, { visibility: 'on' }]
  }
];

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {string} Distance in miles, fixed to 1 decimal place
 */
function findCafeDistance(lat1, lon1, lat2, lon2) {
  const p = 0.017453292519943295; // Math.PI / 180
  const c = Math.cos;
  const a =
    0.5 -
    c((lat2 - lat1) * p) / 2 +
    c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p)) / 2;

  return (7917.6 * Math.asin(Math.sqrt(a))).toFixed(1);
}

/**
 * Sanitize text content to prevent XSS
 * @param {string} str - String to sanitize
 * @returns {string} Sanitized string
 */
function findCafeSanitize(str) {
  if (!str) return '';
  return String(str);
}

/**
 * Create info window content element using safe DOM methods
 * @param {Object} loc - Location object
 * @returns {HTMLElement} Info window content element
 */
function findCafeCreateInfoWindowContent(loc) {
  const container = document.createElement('div');
  container.className = 'find-cafe__info-window';

  const title = document.createElement('p');
  title.className = 'find-cafe__info-window-title';
  title.textContent = findCafeSanitize(loc.name);
  container.appendChild(title);

  const locDiv = document.createElement('div');
  locDiv.className = 'find-cafe__info-window-loc';

  const address = document.createElement('p');
  address.className = 'find-cafe__info-window-address';
  address.textContent = findCafeSanitize(loc.address);
  locDiv.appendChild(address);

  const distance = document.createElement('p');
  distance.className = 'find-cafe__info-window-distance';
  distance.textContent = loc.dist + ' miles';
  locDiv.appendChild(distance);

  container.appendChild(locDiv);

  if (loc.url) {
    const urlLink = document.createElement('a');
    urlLink.className = 'find-cafe__info-window-url';
    const cleanUrl = findCafeSanitize(loc.url).replace('https://', '').replace('http://', '');
    urlLink.href = 'https://' + cleanUrl;
    urlLink.target = '_blank';
    urlLink.rel = 'noopener';
    urlLink.textContent = loc.url;
    container.appendChild(urlLink);
  }

  if (loc.brands) {
    const brandsDiv = document.createElement('div');

    const availableLabel = document.createElement('p');
    availableLabel.className = 'find-cafe__info-window-available';
    availableLabel.textContent = 'Available roasters at location:';
    brandsDiv.appendChild(availableLabel);

    const brandText = document.createElement('p');
    brandText.className = 'find-cafe__info-window-brand';
    brandText.textContent = findCafeSanitize(loc.brands);
    brandsDiv.appendChild(brandText);

    container.appendChild(brandsDiv);
  }

  return container;
}

/**
 * Create result list item element using safe DOM methods.
 * Each result is an <li> holding a <button>, so it can be reached with Tab and
 * activated with Enter or Space. Spans (not divs or p) because a button may only
 * contain phrasing content.
 * @param {Object} loc - Location object
 * @param {string} markerUrl - URL for the marker icon
 * @returns {HTMLElement} List item containing the result button
 */
function findCafeCreateResultElement(loc, markerUrl) {
  const listItem = document.createElement('li');

  const resultElement = document.createElement('button');
  resultElement.type = 'button';
  resultElement.className = 'find-cafe__result';
  resultElement.id = loc.storeid;
  listItem.appendChild(resultElement);

  const markerDiv = document.createElement('span');
  markerDiv.className = 'find-cafe__result-marker';

  const markerImg = document.createElement('img');
  markerImg.src = markerUrl;
  markerImg.alt = '';
  markerImg.width = 22;
  markerImg.height = 28;
  markerDiv.appendChild(markerImg);

  const detailsDiv = document.createElement('span');
  detailsDiv.className = 'find-cafe__result-details';

  const titleP = document.createElement('span');
  titleP.className = 'find-cafe__result-title';
  titleP.textContent = findCafeSanitize(loc.name);
  detailsDiv.appendChild(titleP);

  const addressP = document.createElement('span');
  addressP.className = 'find-cafe__result-address';
  addressP.appendChild(document.createTextNode(findCafeSanitize(loc.street_addr) + ' '));

  const distanceSpan = document.createElement('span');
  distanceSpan.className = 'find-cafe__result-distance';
  distanceSpan.textContent = loc.dist + ' mi';
  addressP.appendChild(distanceSpan);

  detailsDiv.appendChild(addressP);

  resultElement.appendChild(markerDiv);
  resultElement.appendChild(detailsDiv);

  return listItem;
}

/**
 * Main function to fetch and process location data
 * Called as callback when Google Maps API loads
 */
function findCafeGetLocations() {
  const section = document.querySelector('[data-find-cafe]');
  if (!section) return;

  const csvUrl = section.dataset.csvUrl;
  if (!csvUrl) {
    console.error('Find Cafe: No CSV URL provided');
    return;
  }

  fetch(csvUrl)
    .then((response) => response.text())
    .then((data) => findCafeProcessData(data, section))
    .catch((error) => console.error('Find Cafe: Error fetching locations', error));
}

/**
 * Process CSV data and initialize map
 * @param {string} allText - Raw CSV text
 * @param {HTMLElement} section - Section element containing configuration
 */
function findCafeProcessData(allText, section) {
  const markerUrl = section.dataset.markerUrl;

  /* Clean up special characters and split into lines */
  const allTextLines = allText
    .replace(/Caff�/g, 'Caffe')
    .replace(/Caf�/g, 'Cafe')
    .replace(/Coffee�/g, 'Coffee')
    .replace(/"/g, '')
    .split(/\r\n|\n/);

  const headers = allTextLines[0].split(',').map((h) => h.toLowerCase());
  const locations = [];

  for (let i = 1; i < allTextLines.length; i++) {
    const row = allTextLines[i].split(',');
    const category = row[headers.indexOf('categories')];

    /* Filter out non-cafe categories */
    if (
      category !== 'Green Coffee Broker' &&
      category !== 'Grocery' &&
      category !== 'Online'
    ) {
      const lat = parseFloat(row[headers.indexOf('latitude')]);
      const lng = parseFloat(row[headers.indexOf('longitude')]);

      /* Skip invalid coordinates */
      if (isNaN(lat) || isNaN(lng)) continue;

      locations.push({
        storeid: 'loc_' + i,
        lat: lat,
        lng: lng,
        address:
          (row[headers.indexOf('address')] || '') +
          (row[headers.indexOf('city')] ? ', ' + row[headers.indexOf('city')] : '') +
          (row[headers.indexOf('prov/state')] ? ', ' + row[headers.indexOf('prov/state')] : '') +
          (row[headers.indexOf('postal/zip code')] ? ', ' + row[headers.indexOf('postal/zip code')] : ''),
        street_addr: row[headers.indexOf('address')] || '',
        url: row[headers.indexOf('url')],
        category: category,
        name: row[headers.indexOf('store')],
        brands: row[headers.indexOf('brands')] || ''
      });
    }
  }

  findCafeInitMap(locations, markerUrl, section);
}

/**
 * Initialize Google Map with locations
 * @param {Array} locations - Array of location objects
 * @param {string} markerUrl - URL for the marker icon
 * @param {HTMLElement} section - Section element
 */
function findCafeInitMap(locations, markerUrl, section) {
  /* Default center - Seattle, WA */
  let user = { lng: -122.3321, lat: 47.6062 };

  const mapElement = section.querySelector('[data-find-cafe-map]');
  if (!mapElement) return;

  /* Create the map */
  const map = new google.maps.Map(mapElement, {
    zoom: 10,
    center: user,
    styles: findCafeMapStyle,
    disableDefaultUI: true
  });

  /* Add custom zoom controls */
  const zoomControlsDiv = document.createElement('div');
  zoomControlsDiv.classList.add('find-cafe__zoom-controls');
  map.controls[google.maps.ControlPosition.TOP_LEFT].push(zoomControlsDiv);

  const zoomInButton = document.createElement('button');
  zoomInButton.textContent = '+';
  zoomInButton.classList.add('find-cafe__zoom-in');
  zoomInButton.setAttribute('aria-label', 'Zoom in');
  zoomControlsDiv.appendChild(zoomInButton);

  const zoomOutButton = document.createElement('button');
  zoomOutButton.textContent = '-';
  zoomOutButton.classList.add('find-cafe__zoom-out');
  zoomOutButton.setAttribute('aria-label', 'Zoom out');
  zoomControlsDiv.appendChild(zoomOutButton);

  zoomInButton.addEventListener('click', function () {
    map.setZoom(map.getZoom() + 1);
  });

  zoomOutButton.addEventListener('click', function () {
    map.setZoom(map.getZoom() - 1);
  });

  /* Setup geocoder for search */
  const geocoder = new google.maps.Geocoder();
  const searchInput = section.querySelector('[data-find-cafe-search-input]');
  const searchButton = section.querySelector('[data-find-cafe-search-button]');

  const resultsLabel = section.querySelector('[data-find-cafe-results-label]');
  const resultsStatus = section.querySelector('[data-find-cafe-status]');
  let pendingAnnouncement = null;

  /**
   * Tell screen reader users what a search returned, and relabel the list so it
   * no longer says "Closest to you" when the results are for a searched place.
   * Results are filtered by the map's bounds, so when the map is moving this waits
   * for it to settle; when it isn't (same place searched again) there is no idle
   * event, so it announces straight away.
   * @param {string} query - What the user searched for
   * @param {boolean} mapIsMoving - Whether the search moved the map's center
   */
  function announceSearchResults(query, mapIsMoving) {
    /* Drop a wait left over from an earlier search so it can't fire later with old text */
    if (pendingAnnouncement) {
      google.maps.event.removeListener(pendingAnnouncement);
      pendingAnnouncement = null;
    }

    function announce() {
      if (resultsLabel) resultsLabel.textContent = 'Near ' + query + ':';
      if (resultsStatus) {
        /* Clear, then set after a beat: screen readers skip a status that doesn't change */
        resultsStatus.textContent = '';
        setTimeout(function () {
          resultsStatus.textContent =
            stores.length === 1 ? '1 cafe near ' + query : stores.length + ' cafes near ' + query;
        }, 100);
      }
    }

    if (mapIsMoving) {
      pendingAnnouncement = google.maps.event.addListenerOnce(map, 'idle', function () {
        pendingAnnouncement = null;
        announce();
      });
    } else {
      announce();
    }
  }

  if (searchButton && searchInput) {
    searchButton.addEventListener('click', function () {
      const address = searchInput.value;
      if (address) {
        geocoder.geocode({ address: address }, function (results, status) {
          if (status === 'OK') {
            const geo = results[0].geometry.location;
            user.lng = geo.lng();
            user.lat = geo.lat();
            const mapIsMoving = !geo.equals(map.getCenter());
            map.setCenter(geo);
            setMarkers();
            announceSearchResults(address, mapIsMoving);
          } else {
            alert('Could not find that location. Please try a different address.');
          }
        });
      }
    });

    /* Allow Enter key to trigger search */
    searchInput.addEventListener('keypress', function (e) {
      if (e.key === 'Enter') {
        searchButton.click();
      }
    });
  }

  /* Try to get user's location */
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        user = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        map.setCenter(user);
      },
      () => {
        console.log('Find Cafe: Geolocation not available or denied');
      }
    );
  }

  let stores = [];
  let markers = [];
  let oldcenter = new google.maps.LatLng(0, 0);
  const infowindow = new google.maps.InfoWindow();

  /* Close info window when clicking on map */
  google.maps.event.addListener(map, 'click', function () {
    infowindow.close();
  });

  const resultsContainer = section.querySelector('[data-find-cafe-results]');

  /**
   * Update markers and results based on current map bounds
   */
  function setMarkers() {
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const minx = sw.lng();
    const maxx = ne.lng();
    const miny = sw.lat();
    const maxy = ne.lat();

    /* Filter locations within bounds */
    const newStores = locations.filter(
      (loc) => loc.lng > minx && loc.lng < maxx && loc.lat > miny && loc.lat < maxy
    );

    /* Update marker visibility */
    for (let i = 0; i < markers.length; i++) {
      const loc = markers[i];
      const visible =
        loc.lng >= minx && loc.lng <= maxx && loc.lat >= miny && loc.lat <= maxy;
      markers[i].setMap(visible ? map : null);
    }

    if (newStores !== stores) {
      /* Calculate distances and sort */
      stores = newStores
        .map((store) => ({
          ...store,
          dist: findCafeDistance(store.lat, store.lng, user.lat, user.lng)
        }))
        .sort((a, b) => parseFloat(a.dist) - parseFloat(b.dist));

      /* Clear results */
      if (resultsContainer) {
        while (resultsContainer.firstChild) {
          resultsContainer.removeChild(resultsContainer.firstChild);
        }
      }

      for (let i = 0; i < stores.length; i++) {
        const loc = stores[i];

        /* Build info window content using safe DOM methods */
        const infoContent = findCafeCreateInfoWindowContent(loc);

        /* Create or reuse marker */
        let marker = markers.find((m) => m.storeid === loc.storeid);
        if (!marker) {
          marker = new google.maps.Marker({
            position: { lat: loc.lat, lng: loc.lng },
            map,
            /* Name plus address so the many same-named chain locations can be told apart */
            title: loc.name + ', ' + loc.address,
            icon: markerUrl,
            storeid: loc.storeid,
            storetype: loc.category,
            lat: loc.lat,
            lng: loc.lng
          });
          markers.push(marker);

          /* Add click handler to marker */
          (function(m, content) {
            google.maps.event.addListener(m, 'click', function () {
              infowindow.setContent(content);
              infowindow.open(map, m);
            });
          })(marker, infoContent);
        }

        /* Add result to sidebar */
        if (resultsContainer) {
          const resultItem = findCafeCreateResultElement(loc, markerUrl);
          const resultElement = resultItem.querySelector('.find-cafe__result');

          /* Click handler for result button (also fires on Enter and Space) */
          (function(m, content) {
            resultElement.addEventListener('click', function () {
              /* Remove selected class from all results */
              resultsContainer.querySelectorAll('.selected').forEach(function(el) {
                el.classList.remove('selected');
              });
              /* Add selected class to clicked result */
              this.classList.add('selected');
              /* Open info window */
              infowindow.close();
              infowindow.setContent(content);
              infowindow.open(map, m);
              /* Center map on marker */
              map.panTo(m.getPosition());
            });
          })(marker, infoContent);

          resultsContainer.appendChild(resultItem);
        }
      }
    }
  }

  /* Update markers when map stops moving */
  google.maps.event.addListener(map, 'idle', function () {
    const newcenter = map.getCenter();
    const newc = { lng: newcenter.lng(), lat: newcenter.lat() };
    const old = { lng: oldcenter.lng(), lat: oldcenter.lat() };

    if (Math.abs(newc.lng - old.lng) > 0.1 || Math.abs(newc.lat - old.lat) > 0.1) {
      oldcenter = newcenter;
      setMarkers();
    }
  });

  /* Mobile view toggle handlers */
  const viewToggles = section.querySelectorAll('[data-find-cafe-view]');
  const toolbar = section.querySelector('[data-find-cafe-toolbar]');

  viewToggles.forEach(function(toggle) {
    toggle.addEventListener('click', function () {
      const view = this.dataset.findCafeView;

      /* Update active states */
      viewToggles.forEach(function(t) {
        t.classList.remove('active');
      });
      this.classList.add('active');

      /* Toggle toolbar visibility */
      if (toolbar) {
        if (view === 'list') {
          toolbar.classList.add('open');
        } else {
          toolbar.classList.remove('open');
        }
      }
    });
  });
}
